import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './CryptoStrip.module.css';

const COINS = [
  { symbol: 'BTC', name: 'Bitcoin', icon: '/assets/images/coins/BTC.webp', basePrice: 75796.10, vol: '213M', color: '#F7931A' },
  { symbol: 'ETH', name: 'Ethereum', icon: '/assets/images/coins/ETH.webp', basePrice: 2313.37, vol: '192M', color: '#627EEA' },
  { symbol: 'USDT', name: 'Tether', icon: '/assets/images/coins/USDT.webp', basePrice: 85.64, vol: '88.1M', color: '#26A17B' },
  { symbol: 'BNB', name: 'BNB', icon: '/assets/images/coins/BNB.webp', basePrice: 312.48, vol: '16M', color: '#F3BA2F' },
  { symbol: 'XRP', name: 'XRP', icon: '/assets/images/coins/XRP.webp', basePrice: 1.4969, vol: '6.53M', color: '#00AAE4' },
  { symbol: 'ADA', name: 'Cardano', icon: '/assets/images/coins/ADA.webp', basePrice: 0.4251, vol: '963K', color: '#0033AD' },
  { symbol: 'DOGE', name: 'Dogecoin', icon: '/assets/images/coins/DOGE.webp', basePrice: 0.1672, vol: '1.2M', color: '#C2A633' },
  { symbol: 'LTC', name: 'Litecoin', icon: '/assets/images/coins/LTC.webp', basePrice: 84.32, vol: '4.7M', color: '#BFBBBB' },
];

// Generate smooth mini sparkline SVG path from price history
const Sparkline = ({ points, isPositive }) => {
  const width = 155;
  const height = 36;
  const pad = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;

  // Convert points to coordinates
  const coords = points.map((p, i) => ({
    x: (i / (points.length - 1)) * width,
    y: height - ((p - min) / range) * (height - pad * 2) - pad,
  }));

  // Build smooth cubic bezier path
  let d = `M${coords[0].x.toFixed(1)},${coords[0].y.toFixed(1)}`;
  for (let i = 1; i < coords.length; i++) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C${cpx.toFixed(1)},${prev.y.toFixed(1)} ${cpx.toFixed(1)},${curr.y.toFixed(1)} ${curr.x.toFixed(1)},${curr.y.toFixed(1)}`;
  }

  const fillPath = `${d} L${width},${height} L0,${height} Z`;
  const strokeColor = isPositive ? '#22C55E' : '#EF4444';
  const fillColor = isPositive ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)';

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={styles.sparkline} preserveAspectRatio="none">
      <path d={fillPath} fill={fillColor} />
      <path d={d} fill="none" stroke={strokeColor} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
};

const CryptoStrip = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [coins, setCoins] = useState(() =>
    COINS.map((c) => ({
      ...c,
      price: c.basePrice,
      change: (Math.random() * 6 - 1).toFixed(2),
      history: Array.from({ length: 20 }, () => c.basePrice * (1 + (Math.random() - 0.48) * 0.04)),
    }))
  );

  // Update prices every 3 seconds with realistic fluctuations
  useEffect(() => {
    const interval = setInterval(() => {
      setCoins((prev) =>
        prev.map((coin) => {
          const drift = (Math.random() - 0.48) * 0.003;
          const newPrice = coin.price * (1 + drift);
          const newChange = (((newPrice - coin.basePrice) / coin.basePrice) * 100).toFixed(2);
          const newHistory = [...coin.history.slice(1), newPrice];
          return { ...coin, price: newPrice, change: newChange, history: newHistory };
        })
      );
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const totalVol = useMemo(() => {
    const sum = COINS.reduce((acc, c) => {
      const num = parseFloat(c.vol);
      const mult = c.vol.includes('M') ? 1 : c.vol.includes('K') ? 0.001 : 1;
      return acc + num * mult;
    }, 0);
    return `$${sum.toFixed(0)}M`;
  }, []);

  const formatPrice = (price) => {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(6);
  };

  const handleScroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'right' ? 280 : -280, behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Crypto Futures</h2>
        <div className={styles.actions}>
       
          <div className={styles.scrollButtons}>
            <button className={styles.scrollBtn} onClick={() => handleScroll('left')} aria-label="Scroll left">
              <ChevronLeft size={16} />
            </button>
            <button className={styles.scrollBtn} onClick={() => handleScroll('right')} aria-label="Scroll right">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className={styles.scrollContainer} ref={scrollRef}>
        {coins.map((coin) => {
          const isPositive = parseFloat(coin.change) >= 0;
          return (
            <div key={coin.symbol} className={styles.card} onClick={() => navigate('/trade')}>
              {/* Sparkline background */}
              <div className={styles.cardSparkline}>
                <Sparkline points={coin.history} isPositive={isPositive} />
              </div>

              <div className={styles.cardContent}>
                <div className={styles.cardTop}>
                  <img src={coin.icon} alt={coin.symbol} className={styles.coinIcon} />
                  <div className={styles.cardTopText}>
                    <span className={styles.coinSymbol}>{coin.symbol}</span>
                    <span className={`${styles.coinChange} ${isPositive ? styles.positive : styles.negative}`}>
                      {isPositive ? '+' : ''}{coin.change}%
                    </span>
                  </div>
                </div>

                <div className={styles.coinPrice}>{formatPrice(coin.price)}</div>

                <div className={styles.cardMeta}>
                  <span>24h Vol: <strong className={styles.metaHighlight}>${coin.vol}</strong></span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CryptoStrip;
